import { useCallback, useEffect, useRef } from 'react';
import { tAnsweredSearch } from 'src/pages/MessengerV2/MessageSearch.types';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { useAnalytics } from '../useAnalytics.hook';
import {
  MESSAGE_SEARCH_EVENTS,
  MESSAGE_SEARCH_SETTLE_MS,
  tMessageSearchOpenedProps,
  tMessageSearchOpenTrigger,
  tMessageSearchSettledBy,
  tMessageSearchSurface,
} from './messageSearchAnalytics.constants';
import {
  buildSearchedProps,
  newSearchSessionId,
} from './messageSearchAnalytics.functions';

export type tMessageSearchAnalyticsInput = {
  surface: tMessageSearchSurface;
  /** A search is open: the inbox box holds text, or the in-chat bar is open. */
  isSessionOpen: boolean;
  /**
   * What it means here when a search ends without anyone saying why: the inbox
   * box was emptied (`cleared`), or the in-chat bar went away under the person
   * (`left`).
   */
  endedBy: Extract<tMessageSearchSettledBy, 'cleared' | 'left'>;
  /** The text in the box right now, trimmed. */
  typedTerm: string;
  /** The search the server has answered, from the hook that owns it. */
  answered: tAnsweredSearch | null;
  /** Results loaded for it right now, every page so far. */
  resultCount: number;
  hasMore: boolean;
  /** A further page is on its way, so the count is about to change. */
  isLoadingMore: boolean;
};

/** An answered query that has not settled yet, with what it had on screen. */
type tPendingSearch = {
  term: string;
  firstPageCount: number;
  resultCount: number;
  hasMore: boolean;
};

/**
 * ANA-1: the two message-search events, for ONE search.
 *
 * Call it from the search's provider and nowhere else (MessageSearchProvider,
 * ChatSearchProvider). Each is the single instance of its search, so one
 * gesture is one event; a component that has a gesture to report gets a
 * function from that provider's context instead of calling this.
 *
 * `searched` is one SETTLED query, not one request. A query starts counting
 * when the server has answered it and the box still says the same text. It
 * settles the moment the person acts on its results (`settle`), or after
 * MESSAGE_SEARCH_SETTLE_MS with them on screen. A query that is typed past
 * before that was a fragment and is dropped. Each query settles once per
 * session, so stepping through ten matches is one event.
 *
 * Nothing is emitted for a query the server did not answer: under the minimum,
 * exclusions only, aborted, or failed. The event stamps `has_results` and
 * `first_page_count`, and only an answer can fill them.
 *
 * @returns `settle`, for the gesture that acts on the results, and
 *   `trackOpened`, for the in-chat bar opening.
 */
export function useMessageSearchAnalytics(
  input: tMessageSearchAnalyticsInput,
): {
  settle: (settledBy: tMessageSearchSettledBy) => void;
  trackOpened: (trigger: tMessageSearchOpenTrigger) => void;
} {
  const {
    surface,
    isSessionOpen,
    endedBy,
    typedTerm,
    answered,
    resultCount,
    hasMore,
    isLoadingMore,
  } = input;
  const { trackEvent } = useAnalytics();
  const { currentWorkspaceId } = useWorkspaceContext();

  // Read by the timer and by the unmount cleanup, where a render's own
  // `trackEvent` may be stale. It is the last render's, so an event that leaves
  // on unmount still carries the workspace the search ran in.
  const trackEventRef = useRef(trackEvent);
  trackEventRef.current = trackEvent;

  const sessionIdRef = useRef<string | null>(null);
  const pendingRef = useRef<tPendingSearch | null>(null);
  // Never leaves the browser: only what decides "already counted".
  const settledTermsRef = useRef(new Set<string>());
  const workspaceIdRef = useRef(currentWorkspaceId);
  // The answer that was on screen when the workspace changed. Compared by
  // identity: the searches build a new object for every answer.
  const foreignAnswerRef = useRef<tAnsweredSearch | null>(null);

  // The answer on screen is the one for the text as typed. Any other is the
  // previous query's, dimmed or hidden, and nobody is acting on it.
  const onScreen =
    isSessionOpen && answered?.term === typedTerm ? answered : null;

  const settle = useCallback(
    (settledBy: tMessageSearchSettledBy) => {
      const pending = pendingRef.current;
      const sessionId = sessionIdRef.current;

      if (!pending || !sessionId) return;

      pendingRef.current = null;
      settledTermsRef.current.add(pending.term);
      trackEventRef.current(
        MESSAGE_SEARCH_EVENTS.SEARCHED,
        buildSearchedProps({ surface, ...pending, settledBy, sessionId }),
      );
    },
    [surface],
  );

  const trackOpened = useCallback((trigger: tMessageSearchOpenTrigger) => {
    // Already open: asking again only refocuses the field.
    if (sessionIdRef.current) return;

    sessionIdRef.current = newSearchSessionId();
    trackEventRef.current(MESSAGE_SEARCH_EVENTS.OPENED, {
      trigger,
      search_session_id: sessionIdRef.current,
    } satisfies tMessageSearchOpenedProps);
  }, []);

  // Keeps the session and the pending query in step with the search. One
  // effect, in this order, because the cases overlap: the bar closing also
  // empties the answer, and that must read as "left", not as "typed past".
  useEffect(() => {
    if (workspaceIdRef.current !== currentWorkspaceId) {
      // Another workspace under a search that stayed mounted. What was pending,
      // and the answer still on screen, belong to the previous one, and
      // `trackEvent` already stamps the new one.
      workspaceIdRef.current = currentWorkspaceId;
      pendingRef.current = null;
      settledTermsRef.current.clear();
      sessionIdRef.current = null;
      foreignAnswerRef.current = answered;
    }

    if (!isSessionOpen) {
      settle(endedBy);
      sessionIdRef.current = null;
      settledTermsRef.current.clear();

      return;
    }

    if (!sessionIdRef.current) sessionIdRef.current = newSearchSessionId();

    if (
      !onScreen ||
      onScreen === foreignAnswerRef.current ||
      settledTermsRef.current.has(onScreen.term)
    ) {
      // Typed past, or failed, or another workspace's, or already counted in
      // this session.
      pendingRef.current = null;

      return;
    }

    pendingRef.current = {
      term: onScreen.term,
      firstPageCount: onScreen.firstPageCount,
      resultCount,
      hasMore,
    };
  }, [
    currentWorkspaceId,
    isSessionOpen,
    endedBy,
    answered,
    onScreen,
    resultCount,
    hasMore,
    settle,
  ]);

  // The pause. Declared after the effect above, so it reads the pending query
  // that effect just decided. It restarts when the results change, and waits
  // while a further page is loading: "Seguir buscando" gets to bring its
  // matches before the query is called empty.
  useEffect(() => {
    if (!pendingRef.current || isLoadingMore) return;

    const timer = setTimeout(() => settle('paused'), MESSAGE_SEARCH_SETTLE_MS);

    return () => clearTimeout(timer);
  }, [
    currentWorkspaceId,
    isSessionOpen,
    onScreen,
    resultCount,
    hasMore,
    isLoadingMore,
    settle,
  ]);

  // The provider unmounts with its screen: the page left, the chat modal closed.
  useEffect(() => () => settle('left'), [settle]);

  return { settle, trackOpened };
}
